/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, MouseEvent, useRef } from 'react';
import { Box, ButtonBase } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAtomValue, useSetAtom } from 'jotai';
import { QSTypography } from '../../../../../../shared/components/atoms';
import ArrowExpandIcon from '../../../../../../shared/icons/icons-revamp/arrow_expand';
import { useUserPermissions } from '../../../../../../shared/hooks/use-permission';
import { chatListAtom, customFieldConditionsAtom } from '../../../states/chat-list';
import { customFieldPickerAnchorAtom } from './state';
import { countSelectedOptions, filterCompleteConditions } from './utils';
import useIsCustomFieldFilterAllowed from './hooks/use-is-custom-field-filter-allowed';

const CustomFieldFilterSidebarEntry = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const setAnchor = useSetAtom(customFieldPickerAnchorAtom);
  const conditions = useAtomValue(customFieldConditionsAtom);
  const dispatchConditions = useSetAtom(customFieldConditionsAtom);
  const conversationView = useAtomValue(chatListAtom).conversationView;
  const { canReadChatCustomFields } = useUserPermissions();
  const isAllowed = useIsCustomFieldFilterAllowed();

  const complete = filterCompleteConditions(conditions ?? []);
  const selectedCount = complete.reduce((sum, c) => sum + countSelectedOptions(c), 0);

  if (!isAllowed || !canReadChatCustomFields.hasAccess || conversationView === 'groups') {
    return null;
  }

  const placeholder =
    selectedCount === 0 ? 'Filter by value' : selectedCount === 1 ? '1 Value' : `${selectedCount} Values`;

  const handleClearAll = (e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    dispatchConditions({ type: 'clear' });
  };

  return (
    <Box
      css={css`
        width: 100%;
        padding: 12px 8px 16px 16px;
        background: #ffffff;
      `}
    >
      <QSTypography
        css={css`
          color: rgba(0, 0, 0, 0.6);
          font-family: Inter;
          font-size: 14px;
          font-weight: 500;
          line-height: 21px;
          margin-bottom: 8px;
        `}
      >
        Custom field value
      </QSTypography>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setAnchor(anchorRef.current)}
        css={css`
          width: 100%;
          height: 40px;
          border: 1px solid #e0e0ea;
          border-radius: 4px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 6px 8px 12px;
          text-transform: none;
          &:hover {
            border-color: #2d2d2d;
          }
        `}
      >
        <QSTypography
          css={css`
            font-size: 14px;
            color: #2d2d2d;
            font-weight: 400;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            min-width: 0;
            text-align: left;
          `}
        >
          {placeholder}
        </QSTypography>
        {selectedCount > 0 && (
          <CloseIcon
            onClick={handleClearAll}
            css={css`
              width: 16px;
              min-width: 16px;
              height: 16px;
              min-height: 16px;
              color: rgba(0, 0, 0, 0.54);
              cursor: pointer;
              margin-right: 3px;
            `}
          />
        )}
        <ArrowExpandIcon color="#7a7a7a" size={28} />
      </ButtonBase>
    </Box>
  );
};

export default memo(CustomFieldFilterSidebarEntry);
